import { useState, useEffect } from "react";
import { track } from "./lib/analytics";
import { supabase, BRAND, INK, MUTED, LINE, ROW_LINE, pageTitle, unreadBadge, cardFlush, chip, text, btn, errorBanner, savedText, segWrap, segBtn, SHADOW_POP, SHADOW_MODAL, SCRIM, DANGER_LINE, FAINT, SURFACE, WHITE, HOVER_LINE } from "./constants";
import { Icon, CloseButton, ConfirmBar, MoreMenu, candidatePhase, FunnelArrows } from "./shared";
import { GripVertical } from "lucide-react";
import { useIsMobile } from "./lib/responsive";
import { authFetch } from "./lib/ai";

// Métrica "Interesados" editable (solo admin, posiciones abiertas), como una
// celda más de la franja de la tarjeta. Se guarda al salir del
// campo o con Enter, y comprueba que el update devolvió fila (RLS bloqueado =
// 200 con 0 filas); si no, vuelve al valor anterior y el borde se marca como error.
// Al guardar, la etiqueta pasa un momento a "✓ Guardado" (savedText).
const InterestedPill = ({ pos, t, lang, onSaved, bare = false }) => {
  const initial = pos.metrics?.interested ?? 0;
  const [val, setVal] = useState(String(initial));
  const [state, setState] = useState("idle"); // idle | saving | saved | error
  useEffect(() => { setVal(String(pos.metrics?.interested ?? 0)); }, [pos.id, pos.metrics?.interested]);
  const save = async () => {
    const n = Math.max(0, parseInt(val, 10) || 0);
    setVal(String(n));
    if (n === initial) return;
    setState("saving");
    const { data, error } = await supabase.from("positions").update({ interested: n }).eq("id", pos.id).select("id");
    if (error || !data || data.length === 0) { setVal(String(initial)); setState("error"); return; }
    onSaved(n);
    setState("saved");
    setTimeout(() => setState("idle"), 1500);
  };
  const input = (
    <input type="number" min="0" value={val} disabled={state === "saving"} aria-label={t.interested}
      onChange={e => { setVal(e.target.value); if (state !== "idle") setState("idle"); }}
      onBlur={save}
      onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
      style={{ width: 56, textAlign: bare ? "center" : "left", fontWeight: 800, fontSize: 22, color: BRAND, background: "transparent", border: "none", borderBottom: `1.5px dashed ${state === "error" ? DANGER_LINE : LINE}`, outline: "none", padding: 0, fontFamily: "inherit", MozAppearance: "textfield" }} />
  );
  // En el embudo (bare): solo la cifra, y el aviso de guardado debajo
  if (bare) return (
    <div onClick={e => e.stopPropagation()} title={state === "error" ? (lang === "es" ? "No se pudo guardar" : "Couldn't save") : ""} style={{ cursor: "text" }}>
      {input}
      {state === "saved" && <div style={{ ...savedText, fontSize: 11 }}>{lang === "es" ? "✓ Guardado" : "✓ Saved"}</div>}
    </div>
  );
  // En la franja de la tarjeta (24/10): la cifra en azul, editable, con su etiqueta debajo
  return (
    <div onClick={e => e.stopPropagation()} title={state === "error" ? (lang === "es" ? "No se pudo guardar" : "Couldn't save") : ""} style={{ cursor: "text" }}>
      {input}
      {state === "saved"
        ? <div style={{ ...savedText, marginTop: 2 }}>{lang === "es" ? "✓ Guardado" : "✓ Saved"}</div>
        : <div style={{ ...text.label, fontSize: 11, marginTop: 2 }}>{t.interested}</div>}
    </div>
  );
};

const PositionsView = ({ t, lang, setView, setSelectedPosition, isAdmin = false, candidateStatuses = {}, openPositionsList = [], closedPositionsList = [], deletedPositionsList = [], onClosePosition = () => {}, onDeletePosition = () => {}, onReopenPosition = () => {}, onReorderPositions = async () => true, setOpenPositionsList = () => {}, initialTab = null }) => {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab) return initialTab; // route forced (e.g. came from legacy "open" alias)
    try {
      const saved = localStorage.getItem("rutment_positions_tab");
      if (saved && ["open", "closed", "deleted"].includes(saved)) return saved;
    } catch (e) {}
    return "open";
  });

  useEffect(() => {
    try { localStorage.setItem("rutment_positions_tab", activeTab); } catch (e) {}
  }, [activeTab]);

  const positions = activeTab === "deleted" ? deletedPositionsList : activeTab === "closed" ? closedPositionsList : openPositionsList;
  const closed = activeTab === "closed";
  const deleted = activeTab === "deleted";
  const [confirmCloseId, setConfirmCloseId] = useState(null);
  // Al cerrar, a quién se contrató (23/10): va a Track (outcomes) de esa
  // persona. Es el dato que no se puede reconstruir después.
  const [hire, setHire] = useState({ candidateId: "", note: "" });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [intakeCopied, setIntakeCopied] = useState(null); // positionId that was just copied
  const [sendModal, setSendModal] = useState(null); // { position, link } or null
  const [sendForm, setSendForm] = useState({ to: "", candidateName: "", subject: "", body: "" });
  const [sending, setSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null); // null | 'sent' | 'error'
  const [sendError, setSendError] = useState("");
  // Orden manual de las abiertas, arrastrando. Durante el arrastre la lista se
  // reordena en local (dragOrder); al soltar se guarda en App y en la base.
  const [dragId, setDragId] = useState(null);
  const [dragOrder, setDragOrder] = useState(null); // null | [ids]
  const [reorderMsg, setReorderMsg] = useState("");
  const canReorder = isAdmin && activeTab === "open" && positions.length > 1;
  const shown = dragOrder ? dragOrder.map(id => positions.find(p => p.id === id)).filter(Boolean) : positions;

  const dragOverPos = (e, pos) => {
    if (!dragId) return;
    e.preventDefault();
    if (pos.id === dragId || !dragOrder) return;
    const from = dragOrder.indexOf(dragId), to = dragOrder.indexOf(pos.id);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...dragOrder];
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    setDragOrder(next);
  };

  const dropPositions = async (e) => {
    if (!dragId || !dragOrder) return;
    e.preventDefault();
    const final = dragOrder;
    setDragId(null); setDragOrder(null);
    if (final.join("|") === positions.map(p => p.id).join("|")) return;
    const ok = await onReorderPositions(final.map(id => positions.find(p => p.id === id)).filter(Boolean));
    if (!ok) setReorderMsg(lang === "es" ? "No se pudo guardar el nuevo orden. Inténtalo de nuevo." : "Couldn't save the new order. Please try again.");
  };

  // Móvil: no hay arrastre, se ordena con ↑ / ↓. Guarda igual que soltar.
  const [movingId, setMovingId] = useState(null);
  const moveBy = async (pos, delta) => {
    const from = positions.findIndex(p => p.id === pos.id), to = from + delta;
    if (from < 0 || to < 0 || to >= positions.length || movingId) return;
    setReorderMsg(""); setConfirmCloseId(null); setConfirmDeleteId(null);
    const next = [...positions];
    next.splice(from, 1);
    next.splice(to, 0, pos);
    setMovingId(pos.id);
    const ok = await onReorderPositions(next);
    setMovingId(null);
    if (!ok) setReorderMsg(lang === "es" ? "No se pudo guardar el nuevo orden. Inténtalo de nuevo." : "Couldn't save the new order. Please try again.");
  };
  // Móvil: el desglose de "Revisados" se abre tocando (no hay hover)

  const handleCopyIntakeLink = async (e, pos) => {
    e.stopPropagation();
    try {
      // Get or create intake token for this position
      const { data: existing } = await supabase
        .from("position_intake_tokens")
        .select("token")
        .eq("position_id", pos.id)
        .maybeSingle();

      let token;
      if (existing?.token) {
        token = existing.token;
      } else {
        const { data: created } = await supabase
          .from("position_intake_tokens")
          .insert({ position_id: pos.id })
          .select("token")
          .single();
        token = created?.token;
      }

      if (token) {
        await navigator.clipboard.writeText(`https://www.rutment.com?intake=${token}`);
        setIntakeCopied(pos.id);
        track("intake_form_sent", { method: "link_copied", position: pos.title || "" });
        setTimeout(() => setIntakeCopied(null), 3000);
      }
    } catch(e) { console.error("Intake link error:", e); }
  };

  // Open Send Intake modal — gets/creates token and pre-fills template
  const handleOpenSendModal = async (e, pos) => {
    e.stopPropagation();
    try {
      const { data: existing } = await supabase
        .from("position_intake_tokens").select("token").eq("position_id", pos.id).maybeSingle();
      let token = existing?.token;
      if (!token) {
        const { data: created } = await supabase
          .from("position_intake_tokens").insert({ position_id: pos.id }).select("token").single();
        token = created?.token;
      }
      if (!token) return;
      const link = `https://www.rutment.com?intake=${token}`;

      // Detect language from position (default to English)
      const positionLang = pos.lang || pos.language || lang || "en";
      const isEs = positionLang === "es";
      const clientName = pos.clientName || pos.client?.name || "";
      const greeting = isEs ? "Hola" : "Hi";
      const subject = isEs
        ? `Aplicación: ${pos.title}${clientName ? ` – ${clientName}` : ""}`
        : `Application: ${pos.title}${clientName ? ` – ${clientName}` : ""}`;
      const body = isEs
        ? `${greeting} [Nombre],\n\nTe escribimos desde Rutment. Estamos buscando un ${pos.title}${clientName ? ` para ${clientName}` : ""} y creemos que tu perfil podría encajar.\n\nTe pasamos el link de aplicación:\n${link}\n\nAllí encontrarás toda la información de la posición, la empresa, el proceso de selección, y un formulario corto para postularte.\n\nSi tienes cualquier duda, escríbenos.\n\nUn saludo,\nEquipo Rutment`
        : `${greeting} [Name],\n\nWe're reaching out from Rutment. We're looking for a ${pos.title}${clientName ? ` for ${clientName}` : ""} and we think your profile could be a good fit.\n\nHere's the application link:\n${link}\n\nYou'll find all the details about the position, the company, the selection process, and a short form to apply.\n\nLet us know if you have any questions.\n\nBest,\nThe Rutment team`;

      setSendForm({ to: "", candidateName: "", subject, body });
      setSendStatus(null);
      setSendError("");
      setSendModal({ position: pos, link, language: positionLang, clientName });
    } catch (err) { console.error("Open send modal error:", err); }
  };

  const handleSendEmail = async () => {
    if (!sendModal) return;
    if (!sendForm.to || !sendForm.to.includes("@")) { setSendError(lang === "es" ? "Escribe un email válido" : "Please enter a valid email"); setSendStatus("error"); return; }
    setSending(true);
    setSendStatus(null);
    setSendError("");
    // Replace [Nombre]/[Name] placeholder with the candidate name if provided
    const finalBody = sendForm.candidateName.trim()
      ? sendForm.body.replace(/\[Nombre\]/g, sendForm.candidateName.split(" ")[0]).replace(/\[Name\]/g, sendForm.candidateName.split(" ")[0])
      : sendForm.body;
    try {
      const res = await authFetch("/api/send-intake-email", {
        body: JSON.stringify({
          to: sendForm.to.trim(),
          candidateName: sendForm.candidateName.trim(),
          positionTitle: sendModal.position.title,
          intakeLink: sendModal.link,
          language: sendModal.language,
          clientName: sendModal.clientName,
          customSubject: sendForm.subject,
          customBody: finalBody,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSendStatus("sent");
        track("intake_form_sent", { method: "email", position: sendModal.position.title || "" });
        setTimeout(() => { setSendModal(null); setSendStatus(null); }, 1800);
      } else {
        setSendStatus("error");
        setSendError(data.error || (lang === "es" ? "No se pudo enviar el email" : "Failed to send email"));
      }
    } catch (err) {
      console.error("Send email error:", err);
      setSendStatus("error");
      setSendError(err.message || (lang === "es" ? "Error de red" : "Network error"));
    }
    setSending(false);
  };

  const title = lang === "es" ? "Posiciones" : "Positions";

  const tabs = [
    { id: "open", label: lang === "es" ? "Abiertas" : "Open", count: openPositionsList.length },
    { id: "closed", label: lang === "es" ? "Cerradas" : "Closed", count: closedPositionsList.length },
  ];
  if (isAdmin) tabs.push({ id: "deleted", label: lang === "es" ? "Eliminadas" : "Deleted", count: deletedPositionsList.length });

  // Móvil: botones de acción con blanco de 40 px, repartidos a lo ancho de la tarjeta
  // Móvil: el aviso de confirmación va dentro de la tarjeta, neutro, con color solo en el botón que ejecuta
  // Tarjeta de posición (móvil y escritorio, 24/10): título con el globo de
  // pendientes, cifras en franja y todo lo de admin en «⋯». Tocar o pulsar la
  // tarjeta abre la posición. En escritorio se ordena arrastrando y el admin
  // edita Interesados en su celda.
  const posCard = (pos) => {
    const sts = (pos.candidates || []).map(c => candidateStatuses[c.id] || c.status);
    const pendingCount = sts.filter(x => x === "pending").length;
    const total = sts.length;
    const pct = total ? Math.round((sts.filter(x => x !== "pending").length / total) * 100) + "%" : "–";
    const idx = positions.findIndex(p => p.id === pos.id);
    const validatedCount = sts.filter(x => x === "approved").length;
    const cells = closed || deleted
      ? [[t.qualified, (pos.candidates || []).filter(c => c.scoring && (c.scoring.technical || c.scoring.soft || c.scoring.cultural || c.scoring.growth)).length], [t.validated, validatedCount], [lang === "es" ? "Candidatos" : "Candidates", total]]
      : [[t.pending, pendingCount], [t.liveCandidates, (pos.candidates || []).filter(c => ["approved", "pending"].includes(candidateStatuses[c.id] || c.status)).length], [t.reviewed, pct]];
    // Embudo (escritorio, 24/10): cada cifra cuenta a quien ha llegado AL MENOS
    // a esa fase, para que siempre baje; el % es lo que pasa de la anterior
    const phases = (pos.candidates || []).map(c => candidatePhase(c, candidateStatuses));
    const atLeast = (list) => phases.filter(p => list.includes(p)).length;
    const interested = pos.metrics?.interested ?? 0;
    const funnel = [
      { key: "interested", label: t.interested, n: interested },
      { key: "candidates", label: lang === "es" ? "Candidatos" : "Candidates", n: total },
      { key: "validated", label: t.validated, n: atLeast(["validated", "interview", "references"]) },
      { key: "interview", label: lang === "es" ? "Entrevista" : "Interview", n: atLeast(["interview", "references"]) },
      { key: "references", label: lang === "es" ? "Referencias" : "References", n: atLeast(["references"]) },
    ];
    const rejectedCount = phases.filter(p => p === "rejected").length;
    const endToEnd = interested ? Math.round((funnel[4].n / interested) * 100) : null;
    const noop = { stopPropagation() {} };
    const items = !isAdmin ? [] : (closed || deleted) ? [
      { label: lang === "es" ? "Reabrir" : "Reopen", onClick: () => onReopenPosition(pos) },
    ] : [
      { label: lang === "es" ? "Copiar link de intake" : "Copy intake link", onClick: () => handleCopyIntakeLink(noop, pos) },
      { label: lang === "es" ? "Enviar link de intake" : "Send intake link", onClick: () => handleOpenSendModal(noop, pos) },
      canReorder && isMobile && idx > 0 && { label: lang === "es" ? "Subir" : "Move up", onClick: () => moveBy(pos, -1) },
      canReorder && isMobile && idx < positions.length - 1 && { label: lang === "es" ? "Bajar" : "Move down", onClick: () => moveBy(pos, 1) },
      { label: lang === "es" ? "Cerrar posición" : "Close position", onClick: () => { setHire({ candidateId: "", note: "" }); setConfirmDeleteId(null); setConfirmCloseId(pos.id); } },
      { label: lang === "es" ? "Eliminar" : "Delete", danger: true, onClick: () => { setConfirmCloseId(null); setConfirmDeleteId(pos.id); } },
    ];
    return (
      <div onClick={() => { if (!deleted) { setSelectedPosition(pos); setView("candidates"); } }} style={{ cursor: deleted ? "default" : "pointer" }}>
        <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", gap: 8, padding: isMobile ? "14px 8px 10px 16px" : "16px 12px 14px 20px" }}>
          {canReorder && !isMobile && <span title={lang === "es" ? "Arrastra para ordenar" : "Drag to reorder"} style={{ display: "inline-flex", marginLeft: -10, cursor: "grab" }}><GripVertical size={16} strokeWidth={1.75} color={FAINT} /></span>}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 800, fontSize: 15, color: INK, lineHeight: 1.3 }}>{pos.title}</span>
              {!closed && !deleted && pendingCount > 0 && <span style={unreadBadge}>{pendingCount}</span>}
              {closed && <span style={chip(false)}>{lang === "es" ? "Cerrada" : "Closed"}</span>}
              {deleted && <span style={chip(false)}>{lang === "es" ? "Eliminada" : "Deleted"}</span>}
            </div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
              {t.dateOpening}: {pos.dateOpening}{closed && <> · {lang === "es" ? "Cerrada" : "Closed"}: {pos.dateClosed}</>}
            </div>
            {intakeCopied === pos.id && <div style={{ ...savedText, marginTop: 4 }}>{lang === "es" ? "Link copiado" : "Link copied"}</div>}
          </div>
          {items.length > 0 && <MoreMenu items={items} label={lang === "es" ? "Más acciones" : "More actions"} width={220} />}
        </div>
        {!isMobile ? (
          <div style={{ borderTop: "1px solid " + ROW_LINE, padding: "16px 20px 14px" }}>
            <FunnelArrows lang={lang} stages={funnel.map(f => f.key === "interested" && isAdmin && !closed && !deleted
              ? { ...f, node: <div style={{ display: "flex", justifyContent: "center", marginTop: 2 }}><InterestedPill bare pos={pos} t={t} lang={lang} onSaved={n => setOpenPositionsList(prev => prev.map(p => p.id === pos.id ? { ...p, metrics: { ...p.metrics, interested: n } } : p))} /></div> }
              : f)} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 10, fontSize: 12, color: MUTED }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {/* Sin globo: el rojo ya va junto al título (24/10) */}
                {!closed && !deleted && pendingCount > 0 && <>{pendingCount} {lang === "es" ? "por revisar" : "to review"} · </>}
                {pct} {lang === "es" ? "revisados" : "reviewed"}{rejectedCount > 0 && <> · {rejectedCount} {lang === "es" ? (rejectedCount === 1 ? "descartado" : "descartados") : "rejected"}</>}
              </span>
              {endToEnd !== null && <span>{lang === "es" ? "Del interés a referencias" : "From interest to references"}: {endToEnd}%</span>}
            </div>
          </div>
        ) : (
        <div style={{ display: "flex", borderTop: "1px solid " + ROW_LINE }}>
          {cells.map(([l, v], k) => (
            <div key={l} style={{ flex: "1 1 0", minWidth: 0, textAlign: isMobile ? "center" : "left", padding: isMobile ? "12px 4px" : "12px 20px", borderLeft: k ? "1px solid " + ROW_LINE : "none" }}>
              <>
                <div style={{ fontSize: isMobile ? 16 : 22, fontWeight: 800, color: INK, fontVariantNumeric: "tabular-nums" }}>{v}</div>
                <div style={{ ...text.label, fontSize: 11, marginTop: 2 }}>{l}</div>
              </>
            </div>
          ))}
        </div>
        )}
      </div>
    );
  };
  const mConfirm = { position: "static", background: SURFACE, border: "none", borderTop: "1px solid " + ROW_LINE, borderRadius: 0, padding: 16, boxShadow: "none", minWidth: 0 };
  const mConfirmBtn = { minHeight: 44, fontSize: 13, fontFamily: "inherit" };
  // Escritorio: la confirmación flota bajo los botones de la tarjeta
  const dConfirm = { position: "absolute", top: "calc(100% - 10px)", right: 40, zIndex: 300, minWidth: 300, maxWidth: 460, borderRadius: 12, padding: "14px 16px", boxShadow: SHADOW_POP };
  // Móvil: campos de 44 px y letra 16 (iOS amplía la página por debajo de 16)
  const mInput = isMobile ? { minHeight: 44, fontSize: 16 } : {};

  return (
  <div style={{ padding: isMobile ? 16 : 32 }}>
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "flex-start", gap: isMobile ? 14 : undefined, marginBottom: isMobile ? 16 : 24 }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={pageTitle(isMobile, 4)}>{title}</h1>
        {/* El recuento ya va en la pestaña: el subtítulo solo explica cómo ordenar (24/10) */}
        {!isMobile && canReorder && <p style={{ ...text.meta, margin: 0 }}>{canReorder && (lang === "es" ? "Arrastra para ordenarlas" : "Drag to reorder")}</p>}
        {/* Tabs — en el móvil, a todo el ancho y desplazables */}
        <div style={{ ...segWrap, marginTop: 16, width: isMobile ? "100%" : "fit-content", boxSizing: "border-box", overflowX: isMobile ? "auto" : undefined }}>
          {tabs.map(tab => {
            const active = tab.id === activeTab;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                style={{ ...segBtn(active), display: "flex", alignItems: "center", gap: 6, ...(isMobile ? { flex: "1 0 auto", justifyContent: "center", minHeight: 40, whiteSpace: "nowrap" } : {}) }}>
                {tab.label}
                <span style={{ fontSize: 11, color: MUTED, fontWeight: 600 }}>{tab.count}</span>
              </button>
            );
          })}
        </div>
      </div>
      {activeTab === "open" && (
        <button onClick={() => setView("newPosition")} style={{ ...btn.primary, display: "flex", alignItems: "center", gap: 8, ...(isMobile ? { justifyContent: "center", minHeight: 44, width: "100%" } : {}) }}>
          <Icon name="plus" size={16} color={WHITE} /> {t.newPosition}
        </button>
      )}
    </div>

    {reorderMsg && (
      <div style={{ ...errorBanner, marginBottom: 14 }}>{reorderMsg}</div>
    )}

    <div onDragOver={e => { if (dragId) e.preventDefault(); }} onDrop={dropPositions}>
    {shown.map(pos => (
      <div key={pos.id}
        draggable={canReorder && !isMobile}
        onDragStart={e => { if (!canReorder || isMobile) return; setReorderMsg(""); setConfirmCloseId(null); setConfirmDeleteId(null); setDragId(pos.id); setDragOrder(positions.map(p => p.id)); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(pos.id)); }}
        onDragOver={e => dragOverPos(e, pos)}
        onDragEnd={() => { setDragId(null); setDragOrder(null); }}
        style={{ position: "relative", marginBottom: 12, opacity: dragId === pos.id ? 0.4 : 1, transition: "opacity 0.12s, border-color 0.15s", ...cardFlush }}
        onMouseEnter={isMobile || deleted ? undefined : (e => { e.currentTarget.style.borderColor = HOVER_LINE; })}
        onMouseLeave={isMobile || deleted ? undefined : (e => { e.currentTarget.style.borderColor = LINE; })}>
      {posCard(pos)}
      {/* Close confirmation */}
      {confirmCloseId === pos.id && (
        <div onClick={e => e.stopPropagation()}>
          <ConfirmBar danger={false}
            message={lang === "es" ? <>¿Seguro que quieres cerrar <strong>{pos.title}</strong>?</> : <>Are you sure you want to close <strong>{pos.title}</strong>?</>}
            actionLabel={lang === "es" ? "Confirmar" : "Confirm"}
            cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
            onConfirm={() => { onClosePosition(pos, hire); setConfirmCloseId(null); }}
            onCancel={() => setConfirmCloseId(null)}
            style={isMobile ? mConfirm : dConfirm}>
            <div style={{ flex: "1 1 100%", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <select value={hire.candidateId} onChange={e => setHire(h => ({ ...h, candidateId: e.target.value }))}
                aria-label={lang === "es" ? "¿A quién se contrató?" : "Who was hired?"}
                style={{ flex: "1 1 220px", minWidth: 0, height: isMobile ? 44 : 34, border: `1.5px solid ${LINE}`, borderRadius: 8, padding: "0 10px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", background: WHITE, color: hire.candidateId ? INK : MUTED }}>
                <option value="">{lang === "es" ? "¿A quién se contrató? (opcional)" : "Who was hired? (optional)"}</option>
                {(pos.candidates || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {hire.candidateId && (
                <input value={hire.note} onChange={e => setHire(h => ({ ...h, note: e.target.value }))}
                  placeholder={lang === "es" ? "Incorporación, salario, condiciones…" : "Start date, salary, terms…"}
                  aria-label={lang === "es" ? "Nota de la contratación" : "Hire note"}
                  style={{ flex: "2 1 260px", minWidth: 0, height: isMobile ? 44 : 34, border: `1.5px solid ${LINE}`, borderRadius: 8, padding: "0 10px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box" }} />
              )}
            </div>
          </ConfirmBar>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDeleteId === pos.id && (
        <div onClick={e => e.stopPropagation()}>
          <ConfirmBar
            message={lang === "es" ? <>¿Eliminar <strong>{pos.title}</strong>? Quedará en "Eliminadas".</> : <>Delete <strong>{pos.title}</strong>? It will be kept in "Deleted positions".</>}
            actionLabel={lang === "es" ? "Eliminar" : "Delete"}
            cancelLabel={lang === "es" ? "Cancelar" : "Cancel"}
            onConfirm={() => { onDeletePosition(pos); setConfirmDeleteId(null); }}
            onCancel={() => setConfirmDeleteId(null)}
            style={isMobile ? mConfirm : dConfirm} />
        </div>
      )}
      </div>
    ))}
    </div>

    {/* Send Intake Modal */}
    {sendModal && (
      <div onClick={() => !sending && setSendModal(null)}
        style={{ position: "fixed", inset: 0, background: SCRIM, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: isMobile ? 0 : 20 }}>
        {/* En el móvil, hoja a pantalla completa con su propio desplazamiento */}
        <div onClick={e => e.stopPropagation()}
          style={isMobile
            ? { position: "fixed", inset: 0, background: WHITE, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "0 16px calc(16px + env(safe-area-inset-bottom))", boxSizing: "border-box" }
            : { background: WHITE, borderRadius: 16, width: "100%", maxWidth: 640, maxHeight: "90vh", overflow: "auto", padding: 32, boxShadow: SHADOW_MODAL }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "center" : "flex-start", marginBottom: 6, ...(isMobile ? { position: "sticky", top: 0, background: WHITE, zIndex: 1, minHeight: 56, gap: 8 } : {}) }}>
            <h2 style={{ ...text.cardTitle, margin: 0 }}>
              {lang === "es" ? "Enviar invitación de intake" : "Send intake invitation"}
            </h2>
            <CloseButton onClick={() => !sending && setSendModal(null)} label={lang === "es" ? "Cerrar" : "Close"}
              style={{ cursor: sending ? "not-allowed" : "pointer", ...(isMobile ? { marginRight: -8 } : {}) }} />
          </div>
          <p style={{ margin: "0 0 20px", fontSize: 13, color: MUTED }}>
            {sendModal.position.title}{sendModal.clientName ? ` · ${sendModal.clientName}` : ""}
          </p>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ ...text.label, display: "block", marginBottom: 6 }}>
                {lang === "es" ? "Email del candidato" : "Candidate email"} *
              </label>
              <input type="email" value={sendForm.to}
                onChange={e => { setSendForm(f => ({ ...f, to: e.target.value })); setSendStatus(null); setSendError(""); }}
                placeholder="andres@example.com"
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", ...mInput }}
                onFocus={e => e.target.style.borderColor = BRAND}
                onBlur={e => e.target.style.borderColor = LINE} />
            </div>
            <div>
              <label style={{ ...text.label, display: "block", marginBottom: 6 }}>
                {lang === "es" ? "Nombre (opcional)" : "Name (optional)"}
              </label>
              <input type="text" value={sendForm.candidateName}
                onChange={e => setSendForm(f => ({ ...f, candidateName: e.target.value }))}
                placeholder={lang === "es" ? "Andrés Molina" : "Andrew Smith"}
                style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", ...mInput }}
                onFocus={e => e.target.style.borderColor = BRAND}
                onBlur={e => e.target.style.borderColor = LINE} />
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ ...text.label, display: "block", marginBottom: 6 }}>
              {lang === "es" ? "Asunto" : "Subject"}
            </label>
            <input type="text" value={sendForm.subject}
              onChange={e => setSendForm(f => ({ ...f, subject: e.target.value }))}
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", ...mInput }}
              onFocus={e => e.target.style.borderColor = BRAND}
              onBlur={e => e.target.style.borderColor = LINE} />
          </div>

          <div style={{ marginBottom: 6 }}>
            <label style={{ ...text.label, display: "block", marginBottom: 6 }}>
              {lang === "es" ? "Mensaje" : "Message"}
            </label>
            <textarea value={sendForm.body}
              onChange={e => setSendForm(f => ({ ...f, body: e.target.value }))}
              rows={11}
              style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: 13, outline: "none", fontFamily: "inherit", resize: "vertical", lineHeight: 1.5, color: INK, ...(isMobile ? { fontSize: 16 } : {}) }}
              onFocus={e => e.target.style.borderColor = BRAND}
              onBlur={e => e.target.style.borderColor = LINE} />
            <div style={{ ...text.meta, marginTop: 4 }}>
              {lang === "es" ? "Si rellenas el nombre, sustituirá [Nombre] al enviar" : "If you fill the name, it will replace [Name] when sent"}
            </div>
          </div>

          {/* Status */}
          {sendStatus === "sent" && (
            <div style={{ ...savedText, marginTop: 14 }}>
              ✓ {lang === "es" ? "Email enviado correctamente" : "Email sent successfully"}
            </div>
          )}
          {sendStatus === "error" && (
            <div style={{ ...errorBanner, marginTop: 14 }}>
              {sendError || (lang === "es" ? "Error al enviar" : "Failed to send")}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 22, ...(isMobile ? { flexDirection: "column-reverse" } : {}) }}>
            <button onClick={() => !sending && setSendModal(null)}
              disabled={sending}
              style={{ ...btn.ghost, cursor: sending ? "not-allowed" : "pointer", ...(isMobile ? mConfirmBtn : {}) }}>
              {lang === "es" ? "Cancelar" : "Cancel"}
            </button>
            <button onClick={handleSendEmail}
              disabled={sending || !sendForm.to || sendStatus === "sent"}
              style={{ ...(sending || !sendForm.to || sendStatus === "sent" ? btn.disabled : btn.primary), ...(isMobile ? mConfirmBtn : {}) }}>
              {sending ? (lang === "es" ? "Enviando..." : "Sending...") : (lang === "es" ? "Enviar" : "Send")}
            </button>
          </div>
        </div>
      </div>
    )}

  </div>
  );
};

export default PositionsView;
